import { useState } from 'react';
import { LIGHT, MUTE, FAINT, GHOST, GOLD, EMBER, BASE, SURFACE, MONO } from '../lib/constants';
import { Horizon, Btn, Mo, Bd, Dp, Ic, ProgressArc } from './shared';
import { ShareCard, ShareButton } from './ShareCard';

export function CelebrationScreen({ data }) {
  const [showShare, setShowShare] = useState(false);
  const d = data || {};
  const name = d.debt_name || 'Debt';
  const amount = d.original_amount || 0;
  const time = d.time || '—';
  const payments = d.payments || '—';
  const interest = d.interest_saved || 0;

  return (
    <div data-testid="celebration-screen" style={{ padding: '60px 28px', textAlign: 'center', position: 'relative' }}>
      <Horizon intensity={1.0} />
      <div className="dettio-screen__inner">
        <div style={{ animation: 'celebIn 0.6s ease-out' }}>
          <div style={{ marginBottom: 16 }}>{Ic.skull(60, GOLD)}</div>
          <Dp style={{ fontSize: 32, fontWeight: 800, color: 'var(--gold)', display: 'block', letterSpacing: -1 }}>DEBT KILLED</Dp>
        </div>
        <div style={{ marginTop: 16, marginBottom: 6 }}>
          <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 2 }}>{name.toUpperCase()}</Mo>
        </div>
        <Dp style={{ fontSize: 42, fontWeight: 800, color: 'var(--light)', display: 'block', letterSpacing: -2 }}>${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Dp>
        <Bd style={{ fontSize: 14, color: 'var(--mute)', display: 'block', marginTop: 4 }}>Conquered in {time}</Bd>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 24 }}>
          {[{ l: 'INTEREST', v: `$${interest}`, c: GOLD }, { l: 'PAYMENTS', v: `${payments}`, c: LIGHT }, { l: 'TIME', v: time, c: GOLD }].map(s => (
            <div key={s.l} style={{ flex: 1, padding: 12, background: 'var(--faint)', textAlign: 'center' }}>
              <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 4 }}>{s.l}</Mo>
              <Mo style={{ fontSize: 12, fontWeight: 700, color: s.c }}>{s.v}</Mo>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 24, padding: 16, background: `${GOLD}08`, border: `1px solid ${GOLD}15` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
            <span className="emb-p">{Ic.flame(16, GOLD)}</span>
            <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--gold)', letterSpacing: 1 }}>+200 POINTS EARNED</Mo>
          </div>
          <Bd style={{ fontSize: 12, color: 'var(--mute)', lineHeight: 1.6 }}>
            {d.is_debt_free
              ? "You're completely DEBT FREE! Every single dollar — conquered."
              : `$${amount.toLocaleString()} now rolls into your next target. Keep the momentum going.`}
          </Bd>
        </div>
        <ShareButton onClick={() => setShowShare(true)} />
        {showShare && (
          <ShareCard
            type={d.is_debt_free ? 'debtFree' : 'debtKilled'}
            stats={{
              debtName: name,
              amount,
              time,
              interestSaved: interest,
              payments: `${payments}`,
              totalKilled: `$${amount.toLocaleString()}`,
              shareText: d.is_debt_free
                ? `I'm officially DEBT FREE! $${amount.toLocaleString()} conquered with Dettio!`
                : `Just killed my ${name} debt — $${amount.toLocaleString()} conquered in ${time} with Dettio!`,
            }}
            onClose={() => setShowShare(false)}
          />
        )}
      </div>
    </div>
  );
}

export function ComebackScreen() {
  return (
    <div data-testid="comeback-screen" style={{ padding: '60px 28px', textAlign: 'center', position: 'relative' }}>
      <Horizon intensity={0.8} ember />
      <div className="dettio-screen__inner">
        <div className="emb-p" style={{ marginBottom: 16 }}>{Ic.flame(60, EMBER)}</div>
        <Dp style={{ fontSize: 28, fontWeight: 800, color: 'var(--light)', display: 'block', letterSpacing: -1 }}>Welcome back</Dp>
        <Bd style={{ fontSize: 14, color: 'var(--mute)', display: 'block', marginTop: 6, marginBottom: 28 }}>Your streak reset, but your progress didn't. Let's rebuild.</Bd>
        <div style={{ padding: 20, background: 'var(--surface)', border: `1px solid ${FAINT}`, marginBottom: 16 }}>
          <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 2, display: 'block', marginBottom: 12 }}>SINCE YOU LEFT</Mo>
          <div style={{ display: 'flex', gap: 12 }}>
            {[{ l: 'MISSED', v: '2', c: EMBER }, { l: 'INTEREST', v: '+$84', c: EMBER }, { l: 'PROGRESS', v: '24%', c: GOLD }].map(s => (
              <div key={s.l} className="dettio-flex-1">
                <Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block', marginBottom: 4 }}>{s.l}</Mo>
                <Mo style={{ fontSize: 18, fontWeight: 700, color: s.c }}>{s.v}</Mo>
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding: 14, background: `${EMBER}12`, border: `1px solid ${EMBER}25` }}>
          <Bd style={{ fontSize: 12, color: 'var(--mute)', lineHeight: 1.6 }}>Two missed payments means $84 in extra interest. But here's the thing — you're still 24% free. That progress is real and permanent.</Bd>
        </div>
      </div>
    </div>
  );
}

export function FinaleScreen({ data }) {
  const [showShare, setShowShare] = useState(false);
  const d = data || {};
  return (
    <div data-testid="finale-screen" style={{ padding: '40px 28px', textAlign: 'center', position: 'relative' }}>
      <Horizon intensity={1.2} />
      <div className="dettio-screen__inner">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}><ProgressArc value={100} /></div>
        <div style={{ animation: 'celebIn 0.8s ease-out' }}>
          <Dp style={{ fontSize: 38, fontWeight: 800, color: 'var(--gold)', display: 'block', letterSpacing: -2 }}>DEBT FREE</Dp>
        </div>
        <Bd style={{ fontSize: 14, color: 'var(--mute)', display: 'block', margin: '8px 0 24px' }}>Every single dollar — conquered.</Bd>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 24 }}>
          {[{ l: 'TOTAL KILLED', v: d.totalKilled || '$0', c: GOLD }, { l: 'INTEREST SAVED', v: d.interestSaved || '$0', c: GOLD }, { l: 'TIME', v: d.time || '—', c: LIGHT }, { l: 'DEBTS KILLED', v: d.debtsKilled || '0', c: GOLD }].map(s => (
            <div key={s.l} style={{ padding: 14, background: 'var(--faint)', textAlign: 'center' }}>
              <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 4 }}>{s.l}</Mo>
              <Mo style={{ fontSize: 16, fontWeight: 700, color: s.c }}>{s.v}</Mo>
            </div>
          ))}
        </div>
        <ShareButton onClick={() => setShowShare(true)} label="SHARE YOUR FREEDOM" />
        {showShare && <ShareCard type="debtFree" stats={{ totalKilled: d.totalKilled || '$0', interestSaved: d.interestSaved || '$0', time: d.time || '—', badges: d.badges || '—', shareText: `I'm officially DEBT FREE! ${d.totalKilled || ''} conquered with Dettio!` }} onClose={() => setShowShare(false)} />}
      </div>
    </div>
  );
}

export function YearReviewScreen() {
  const [showShare, setShowShare] = useState(false);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const payments = [420, 486, 512, 1200, 486, 486, 586, 486, 486, 720, 486, 486];
  const maxP = Math.max(...payments);

  return (
    <div data-testid="year-review-screen" className="dettio-screen">
      <Horizon intensity={0.6} />
      <div className="dettio-screen__inner">
        <div className="dettio-screen__center" style={{ marginBottom: 24 }}>
          <Dp style={{ fontSize: 24, fontWeight: 800, color: 'var(--light)', display: 'block' }}>2025 Year in Review</Dp>
          <Bd style={{ fontSize: 13, color: 'var(--mute)', marginTop: 4 }}>Your financial freedom journey so far</Bd>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 24 }}>
          {[{ l: 'TOTAL PAID', v: '$6,840', c: GOLD }, { l: 'DEBTS KILLED', v: '1', c: GOLD }, { l: 'INTEREST SAVED', v: '$1,847', c: GOLD }, { l: 'BEST MONTH', v: 'April', c: LIGHT }].map(s => (
            <div key={s.l} style={{ padding: 14, background: 'var(--faint)', textAlign: 'center' }}>
              <Mo style={{ fontSize: 9, color: 'var(--mute)', letterSpacing: 1, display: 'block', marginBottom: 4 }}>{s.l}</Mo>
              <Mo style={{ fontSize: 16, fontWeight: 700, color: s.c }}>{s.v}</Mo>
            </div>
          ))}
        </div>
        <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 2, display: 'block', marginBottom: 10 }}>MONTHLY PAYMENTS</Mo>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 100, marginBottom: 8, padding: '0 4px' }}>
          {payments.map((p, i) => (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{ width: '100%', height: `${(p / maxP) * 80}px`, background: i === 3 ? GOLD : i === 9 ? GOLD : FAINT, minHeight: 4 }} />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4, padding: '0 4px' }}>
          {months.map(m => <div key={m} style={{ flex: 1, textAlign: 'center' }}><Mo style={{ fontSize: 9, color: 'var(--mute)', display: 'block' }}>{m}</Mo></div>)}
        </div>
        <div style={{ marginTop: 20, padding: 14, background: `${GOLD}08`, border: `1px solid ${GOLD}15` }}>
          <Mo style={{ fontSize: 9, fontWeight: 600, color: 'var(--gold)', letterSpacing: 0.5, lineHeight: 1.6 }}>At this pace, you'll be completely debt-free by April 2027. That's 14 months away. Keep going.</Mo>
        </div>
        <ShareButton onClick={() => setShowShare(true)} label="SHARE YOUR YEAR" />
        {showShare && <ShareCard type="yearReview" stats={{ year: '2025', totalPaid: '$6,840', debtsKilled: '1', interestSaved: '$1,847', bestMonth: 'April', shareText: 'My 2025 financial freedom year in review: $6,840 paid, 1 debt killed, $1,847 in interest saved!' }} onClose={() => setShowShare(false)} />}
      </div>
    </div>
  );
}
